import React, { useState, useEffect, useRef, useMemo } from "react";
import { BsSearch } from "react-icons/bs";

interface Options {
  title: string;
  value: string;
}

interface SearchableDropdownProps {
  options: Array<Options>;
  selected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  selected,
  setSelected,
  className = "",
  disabled = false,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Memoize filtered options to avoid unnecessary filtering
  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    return options.filter((opt) =>
      opt.title.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-focus search input on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handlers
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) setSearchText("");
  };

  const onOptionClick = (option: Options) => {
    setSelected(option.value);
    setIsOpen(false);
    setSearchText("");
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
        className={`w-full flex justify-between items-center px-3 py-2.5 text-sm rounded-md border
          transition-colors duration-300 ${
            disabled
              ? "bg-gray-100  text-gray-400 border-[#EA33C0] "
              : isOpen
              ? "bg-[#EA33C0] border-[#EA33C0] text-white"
              : "bg-white border-pink-400 text-gray-400 hover:text-[#EA33C0] focus:outline-none focus:ring-2 focus:ring-pink-500"
          }`}
      >
        <span
          className={`block truncate max-w-full text-left ${
            selected
              ? "text-black/70 pt-1"
              : isOpen
              ? "text-white pt-1"
              : "text-gray-400 pt-1"
          }`}
        >
          {options.find((e) => e.value === selected)?.title ||
            (placeholder ? placeholder : "Select option...")}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          fill="none"
          stroke={isOpen ? "white" : "#EA33C0"}
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute mt-1 w-full bg-white border border-pink-400 rounded-md shadow-lg z-20">
          <div className="p-2">
            <div className="relative text-gray-500">
              <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-3 py-1 border border-[#C72BA4] hover:border-[#EA33C0] focus:ring-[#C72BA4] focus:border-2 rounded-lg focus:outline-none transition"
              />
            </div>
          </div>

          <ul role="listbox" className="max-h-40 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.title}
                  // role="option"
                  // aria-selected={selected === option}
                  className={`cursor-pointer px-3 py-2 text-sm text-gray-800 hover:text-[#EA33C0]`}
                  onClick={() => onOptionClick(option)}
                >
                  {option.title}
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-gray-500 italic select-none">
                No options found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
